import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function emptyForm() {
  return { name: '', dossier_number: '', client_id: '', status: 'ouvert', notes: '', hourly_rate: '' };
}

export default function DossiersPage() {
  const [dossiers, setDossiers] = useState([]);
  const [clients, setClients] = useState([]);
  const [fClient, setFClient] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    try {
      const params = {};
      if (fClient) params.client_id = fClient;
      if (fStatus) params.status = fStatus;
      const [dRes, cRes] = await Promise.all([
        api.get('/dossiers', { params }),
        api.get('/clients'),
      ]);
      setDossiers(dRes.data);
      setClients(cRes.data);
    } catch (err) { console.error(err); }
  }, [fClient, fStatus]);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
    // Pré-remplir le prochain numéro automatique (modifiable, à effacer pour un ancien dossier)
    try {
      const r = await api.get('/dossiers/next-number');
      if (r.data && r.data.number) setForm(f => ({ ...f, dossier_number: r.data.number }));
    } catch { /* ignore */ }
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name || '',
      dossier_number: d.dossier_number || '',
      client_id: d.client_id || '',
      status: d.status || 'ouvert',
      notes: d.notes || '',
      hourly_rate: d.hourly_rate || '',
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('L\'intitulé du dossier est requis.'); return; }
    const payload = {
      name: form.name.trim(),
      dossier_number: form.dossier_number || null,
      client_id: form.client_id || null,
      status: form.status,
      notes: form.notes || null,
      hourly_rate: form.hourly_rate === '' ? null : form.hourly_rate,
    };
    try {
      if (editingId) await api.put(`/dossiers/${editingId}`, payload);
      else await api.post('/dossiers', payload);
      setShowModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer le dossier « ${d.name} » de la liste ?\n(Les prestations et factures liées ne sont pas supprimées.)`)) return;
    await api.delete(`/dossiers/${d.id}`);
    load();
  };

  const filtered = search
    ? dossiers.filter(d => (d.name + ' ' + (d.dossier_number || '') + ' ' + (d.client_name || '')).toLowerCase().includes(search.toLowerCase()))
    : dossiers;

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Dossiers</h1>
          <p>{dossiers.length} dossier{dossiers.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nouveau dossier</button>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <input className="form-input" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: '0.75rem' }}>Client</label>
          <select className="form-input" value={fClient} onChange={e => setFClient(e.target.value)} style={{ maxWidth: 220 }}>
            <option value="">Tous les clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: '0.75rem' }}>Statut</label>
          <select className="form-input" value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ maxWidth: 150 }}>
            <option value="">Tous</option>
            <option value="ouvert">Ouverts</option>
            <option value="cloture">Clôturés</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">📁</div>
          <p>{dossiers.length === 0 ? 'Aucun dossier. Créez votre premier dossier !' : 'Aucun résultat.'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {filtered.map(d => (
            <div key={d.id} className="card" style={{ cursor: 'pointer', padding: 16, opacity: d.status === 'cloture' ? 0.65 : 1 }} onClick={() => openEdit(d)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 2 }}>
                    📁 {d.name}
                    {d.dossier_number && <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-bg)', padding: '1px 6px', borderRadius: 6 }}>N° {d.dossier_number}</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                    {d.client_name ? `👥 ${d.client_name}` : <span style={{ color: 'var(--text-muted)' }}>Aucun client lié</span>}
                  </div>
                  {parseFloat(d.hourly_rate) > 0 && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>⏱️ {fmtMoney(d.hourly_rate)}/h</div>
                  )}
                </div>
                <span className="badge" style={{ background: d.status === 'cloture' ? '#f1f5f9' : '#ecfdf5', color: d.status === 'cloture' ? '#64748b' : '#10b981' }}>
                  {d.status === 'cloture' ? '🔒 Clôturé' : '✅ Ouvert'}
                </span>
              </div>
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>À facturer</span>
                <span style={{ fontWeight: 700, color: parseFloat(d.a_facturer) > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>{fmtMoney(d.a_facturer)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>{editingId ? 'Modifier le dossier' : 'Nouveau dossier'}</h2>
              <button type="button" className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={save}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Intitulé du dossier *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required autoFocus placeholder="Ex : Kazimierski / MP" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>N° dossier</label>
                  <input className="form-input" value={form.dossier_number} onChange={e => setForm({ ...form, dossier_number: e.target.value })} placeholder="Ex : 2600946" />
                </div>
              </div>
              {!editingId && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: -8, marginBottom: 10 }}>
                  💡 Le N° dossier est <strong>pré-rempli automatiquement</strong>. Pour un <strong>ancien</strong> dossier (déjà dans B4T), efface-le et colle son numéro B4T.
                </div>
              )}

              <div className="form-group">
                <label>Client</label>
                <select className="form-input" value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}>
                  <option value="">— Sélectionner un client existant —</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.type === 'societe' ? '🏢' : '👤'} {c.name}{c.client_number ? ` (N° ${c.client_number})` : ''}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  Le client doit d'abord exister dans <strong>Clients</strong>. Choisis-le ici pour lier le dossier.
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Statut</label>
                  <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="ouvert">✅ Ouvert</option>
                    <option value="cloture">🔒 Clôturé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tarif horaire (€)</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={form.hourly_rate}
                    onChange={e => setForm({ ...form, hourly_rate: e.target.value })}
                    placeholder="Ex : 180 — prioritaire sur le tarif du client" />
                </div>
              </div>

              <div className="form-group">
                <label>Notes (optionnel)</label>
                <textarea className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ minHeight: 56 }} />
              </div>

              <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
                {editingId
                  ? <button type="button" className="btn-ghost" onClick={() => { remove({ id: editingId, name: form.name }); setShowModal(false); }}>🗑️ Supprimer</button>
                  : <span />}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                  <button type="submit" className="btn btn-primary">{editingId ? 'Enregistrer' : 'Créer le dossier'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
